import { useState } from "react";
import { ArrowRight, Truck } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const INDIAN_MOBILE_RE = /^(?:\+?91[\s-]?)?[6-9]\d{9}$/;
const INDIAN_PIN_RE = /^[1-9]\d{5}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY = {
  full_name: "", mobile: "", email: "", address_line1: "", address_line2: "",
  landmark: "", city: "", district: "", state: "", pin_code: "",
  country: "India", alternate_phone: "", order_notes: "",
};

function validate(v) {
  const e = {};
  if (!v.full_name.trim()) e.full_name = "Please enter your full name.";
  if (!INDIAN_MOBILE_RE.test(v.mobile.trim())) e.mobile = "Please enter a valid 10-digit Indian mobile number.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address.";
  if (v.address_line1.trim().length < 3) e.address_line1 = "Please enter your house/flat no. and street.";
  if (!v.address_line2.trim()) e.address_line2 = "Please enter your area or locality.";
  if (!v.city.trim()) e.city = "City is required.";
  if (!v.state.trim()) e.state = "State is required.";
  if (!INDIAN_PIN_RE.test(v.pin_code.trim())) e.pin_code = "Please enter a valid 6-digit PIN code.";
  if (v.alternate_phone.trim() && !INDIAN_MOBILE_RE.test(v.alternate_phone.trim())) {
    e.alternate_phone = "Please enter a valid 10-digit Indian mobile number.";
  }
  return e;
}

/** Hoisted outside DeliveryForm so its identity never changes between
 * renders — defining it inside the component body (as before) created a
 * brand-new component type on every keystroke, which made React unmount
 * and remount the <Input/>, dropping focus after a single character. */
function Field({ field, label, required, placeholder, value, error, onChange, accentColor, children }) {
  return (
    <div data-field={field}>
      <Label htmlFor={field} className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5C6470]">
        {label} {required && <span style={{ color: accentColor }}>*</span>}
      </Label>
      {children || (
        <Input
          id={field}
          data-testid={`delivery-field-${field.replace(/_/g, "-")}`}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]"
        />
      )}
      {error && (
        <p data-testid={`delivery-error-${field.replace(/_/g, "-")}`} className="mt-1 text-[12px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Shared delivery/contact details form for the Chinnu physical-order
 * flow (Command 04 §11/§12). Pure client-side mirror of the backend's
 * authoritative DeliveryDetails validation — the server re-validates
 * everything again before a Razorpay order is ever created.
 */
export default function DeliveryForm({ initialValue, onContinue, accentColor = "#F57F17" }) {
  const [v, setV] = useState({ ...EMPTY, ...(initialValue || {}) });
  const [errors, setErrors] = useState({});

  const set = (field) => (e) => setV((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate(v);
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      // Empty optional fields are sent as undefined, never "", so the
      // backend's optional-field validators (which only skip None) never
      // see an empty string and reject it.
      const payload = { ...v };
      ["alternate_phone", "landmark", "district", "order_notes"].forEach((f) => {
        if (!payload[f] || !payload[f].trim()) delete payload[f];
      });
      onContinue(payload);
    } else {
      const firstKey = Object.keys(errs)[0];
      document.querySelector(`[data-field="${firstKey}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <form data-testid="delivery-form" onSubmit={handleSubmit} className="space-y-5">
      <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em]" style={{ color: accentColor }}>
        <Truck size={15} /> Delivery &amp; Contact Details
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field field="full_name" label="Full Name" required placeholder="e.g. Priya Reddy" value={v.full_name} error={errors.full_name} onChange={set("full_name")} accentColor={accentColor} />
        <Field field="mobile" label="Mobile Number" required placeholder="10-digit mobile number" value={v.mobile} error={errors.mobile} onChange={set("mobile")} accentColor={accentColor} />
        <Field field="email" label="Email Address" required placeholder="you@example.com" value={v.email} error={errors.email} onChange={set("email")} accentColor={accentColor} />
        <Field field="alternate_phone" label="Alternate Phone" placeholder="Optional" value={v.alternate_phone} error={errors.alternate_phone} onChange={set("alternate_phone")} accentColor={accentColor} />
      </div>

      <Field field="address_line1" label="Address Line 1" required placeholder="House / Flat / Door No. + Street" value={v.address_line1} error={errors.address_line1} onChange={set("address_line1")} accentColor={accentColor} />
      <Field field="address_line2" label="Address Line 2" required placeholder="Area / Locality" value={v.address_line2} error={errors.address_line2} onChange={set("address_line2")} accentColor={accentColor} />
      <Field field="landmark" label="Landmark" placeholder="Optional" value={v.landmark} error={errors.landmark} onChange={set("landmark")} accentColor={accentColor} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field field="city" label="City / Town" required placeholder="e.g. Hyderabad" value={v.city} error={errors.city} onChange={set("city")} accentColor={accentColor} />
        <Field field="district" label="District" placeholder="Optional" value={v.district} error={errors.district} onChange={set("district")} accentColor={accentColor} />
        <Field field="state" label="State / Union Territory" required placeholder="e.g. Telangana" value={v.state} error={errors.state} onChange={set("state")} accentColor={accentColor} />
        <Field field="pin_code" label="PIN Code" required placeholder="6-digit PIN" value={v.pin_code} error={errors.pin_code} onChange={set("pin_code")} accentColor={accentColor} />
      </div>

      <Field field="country" label="Country" accentColor={accentColor}>
        <Input id="country" value="India" disabled data-testid="delivery-field-country" className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A] bg-[#F5F5F4]" />
        <p className="mt-1 text-[12px] text-[#5C6470]">Printed-book delivery is currently available within India only.</p>
      </Field>

      <div data-field="order_notes">
        <Label htmlFor="order_notes" className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5C6470]">Order Notes</Label>
        <Textarea
          id="order_notes"
          data-testid="delivery-field-order-notes"
          value={v.order_notes}
          onChange={set("order_notes")}
          placeholder='Optional — e.g. "Please call before delivery"'
          className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]"
          rows={2}
        />
      </div>

      <button
        type="submit"
        data-testid="delivery-form-continue-btn"
        style={{ backgroundColor: accentColor }}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity"
      >
        Review Your Order <ArrowRight size={14} />
      </button>
    </form>
  );
}
